import { useRef, useEffect, useState } from "react";
import { fetchServices } from "../api/SolutionApi";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const SolutionDropdown = ({ services, onClose, triggerRef, onMouseEnter, onMouseLeave }) => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(null);
  const dropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, triggerRef]);

  const handleServiceClick = async (slug) => {
    try {
      const serviceDetail = await fetchServices(slug);
      if (serviceDetail) {
        navigate(`/solution/${slug}`);
        onClose();
      }
    } catch (err) {
      console.error("Error fetching service detail:", err);
    }
  };

  const handleCategoryClick = (category) => {
    if (isMobile) {
      setActiveCategory(prev => 
        prev?.category_id === category.category_id ? null : category
      );
    }
  };

  const handleCategoryHover = (category) => {
    if (!isMobile) {
      setActiveCategory(category);
    }
  };

  return (
    <div 
      ref={dropdownRef}
      className="absolute left-0 z-50 bg-transparent"
      style={{ top: '100%' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={`${isMobile ? 'w-full' : 'flex'} items-start`}>
        {/* Основное меню */}
        <div className={`${isMobile ? 'w-full' : 'w-[300px]'} border-r border-white/10 bg-[rgb(42,42,42)]`}>
          <ul className="py-2 max-h-[60dvh] overflow-y-auto">
            {services.map((section) => (
              <li
                key={section.category_id}
                className="relative"
                onMouseEnter={() => handleCategoryHover(section)}
              >
                <button
                  type="button"
                  onClick={() => handleCategoryClick(section)}
                  className={`w-full px-4 py-3 text-left transition-all duration-200 ${
                    activeCategory?.category_id === section.category_id
                      ? "text-[#42fbfe] bg-white/10"
                      : "text-white hover:text-[#42fbfe] hover:bg-white/5"
                  }`}
                >
                  <span className="flex items-center justify-between">
                    {section.category_name}
                    {section.services.length > 0 && (
                      <svg
                        className={`w-4 h-4 ml-2 transition-transform ${
                          activeCategory?.category_id === section.category_id ? 'text-[#42fbfe]' : ''
                        } ${
                          isMobile && activeCategory?.category_id === section.category_id ? 'rotate-90' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </span>
                </button>

                {/* Мобильное подменю */}
                {isMobile && activeCategory?.category_id === section.category_id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-[rgb(64,64,64)] max-h-[calc(50dvh-48px)] overflow-y-auto"
                  >
                    <ul className="py-2 px-4 space-y-2">
                      {section.services.map((service) => (
                        <li
                          key={service.slug}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleServiceClick(service.slug);
                          }}
                          className="py-2"
                        >
                          <span className="block text-white/80 hover:text-[#42fbfe] transition-colors">
                            {service.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Десктопное подменю */}
        {!isMobile && (
          <AnimatePresence>
            {activeCategory && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="w-[350px] p-4 max-h-[calc(100dvh-102px)] overflow-y-auto bg-[rgb(42,42,42)]"
              >
                <h3 className="text-[#42fbfe] font-semibold mb-4">
                  {activeCategory.category_name}
                </h3>
                <ul className="space-y-2">
                  {activeCategory.services.map((service) => (
                    <li
                      key={service.slug}
                      className="group"
                      onClick={() => handleServiceClick(service.slug)}
                    >
                      <button className="w-full text-left px-3 py-2 text-white/80 hover:text-[#42fbfe] hover:bg-white/5 rounded transition-all duration-200">
                        <span className="block" title={service.name}>
                          {service.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default SolutionDropdown;